import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import type { StandardEquipmentCatalogue } from "../../../configurator";
import type { RootState } from "../../../redux/appState";
import {
  clearConfigModalErrors,
  closeConfigModal,
  submitConfigModal,
} from "../../../redux/coilConfigurator/actions";
import {
  convertFromMm,
  convertToMm,
  type CoatingUnit,
  type ConfigurationModalFormValues,
  type GeometryUnit,
  type SavedCoilConfiguration,
} from "../../../web-app";
import {
  emptyConfigurationFormValues,
  formValuesFromConfiguration,
} from "../domain/configurationModal";
import InputWithUnitSelect from "../components/InputWithUnitSelect";
import CcButton from "../components/CcButton";
import "./configuration-modal.css";

interface ConfigurationModalProps {
  open: boolean;
  mode: "create" | "edit";
  editId: string | null;
  catalogue: StandardEquipmentCatalogue;
  configurations: SavedCoilConfiguration[];
  fieldErrors: Record<string, string>;
  formError: string | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (values: ConfigurationModalFormValues) => void;
  onClearErrors: () => void;
}

const ConfigurationModal = ({
  open,
  mode,
  editId,
  catalogue,
  configurations,
  fieldErrors,
  formError,
  submitting,
  onClose,
  onSubmit,
  onClearErrors,
}: ConfigurationModalProps) => {
  const editConfig = useMemo(
    () => (editId ? configurations.find((item) => item.id === editId) : undefined),
    [configurations, editId],
  );

  const [values, setValues] = useState<ConfigurationModalFormValues>(() =>
    emptyConfigurationFormValues(catalogue),
  );

  useEffect(() => {
    if (!open) return;
    onClearErrors();
    if (mode === "edit" && editConfig) {
      setValues(formValuesFromConfiguration(editConfig));
      return;
    }
    setValues(emptyConfigurationFormValues(catalogue));
  }, [open, mode, editConfig, catalogue, onClearErrors]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, submitting]);

  const setField = useCallback(
    <K extends keyof ConfigurationModalFormValues>(key: K, value: ConfigurationModalFormValues[K]) => {
      setValues((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleGeometryUnitChange = (
    valueKey: keyof ConfigurationModalFormValues,
    unitKey: keyof ConfigurationModalFormValues,
    newUnit: GeometryUnit | CoatingUnit,
  ) => {
    setValues((prev) => {
      const oldUnit = prev[unitKey] as GeometryUnit | CoatingUnit;
      const raw = String(prev[valueKey] ?? "");
      let nextValue = raw;
      if (raw.trim() !== "") {
        const valueMm = convertToMm(Number(raw), oldUnit);
        nextValue = String(parseFloat(convertFromMm(valueMm, newUnit).toFixed(6)));
      }
      return {
        ...prev,
        [unitKey]: newUnit,
        [valueKey]: nextValue,
      } as ConfigurationModalFormValues;
    });
  };

  const handleSubmit = () => {
    if (submitting) return;
    onSubmit(values);
  };

  if (!open) return null;

  const title = mode === "create" ? "Create Coil Configuration" : "Edit Coil Configuration";
  const submitLabel = mode === "create" ? "Create Configuration" : "Save Changes";

  return (
    <div
      className="dialog-scrim"
      role="presentation"
      onClick={(event) => {
        if (submitting) return;
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="dialog-card dialog-card--wide configuration-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="configuration-modal-title"
      >
        <div className="dialog-header configuration-modal__header">
          <h2 className="dialog-title" id="configuration-modal-title">
            {title}
          </h2>
          <CcButton variant="icon" aria-label="Close modal" onClick={onClose} disabled={submitting}>
            ✕
          </CcButton>
        </div>

        <div className="dialog-body configuration-modal-body">
          <form
            id="configuration-modal-form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              handleSubmit();
            }}
          >
            <div className="modal-sections">
              <div className="modal-section">
                <h3 className="modal-section-title">Configuration</h3>
                <div className="modal-fields-grid">
                  <div className="modal-field">
                    <label htmlFor="modal-config-reference">
                      Reference <span className="required-mark" aria-hidden="true">*</span>
                    </label>
                    <input
                      type="text"
                      id="modal-config-reference"
                      name="configReference"
                      autoComplete="off"
                      value={values.configReference}
                      onChange={(event) => setField("configReference", event.target.value)}
                    />
                    {fieldErrors.configReference && (
                      <span className="field-error" role="alert">
                        {fieldErrors.configReference}
                      </span>
                    )}
                  </div>
                  <div className="modal-field">
                    <label htmlFor="modal-config-project">Project</label>
                    <input
                      type="text"
                      id="modal-config-project"
                      name="configProject"
                      autoComplete="off"
                      value={values.configProject}
                      onChange={(event) => setField("configProject", event.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h3 className="modal-section-title">Equipment</h3>
                <div className="modal-fields-grid">
                  <div className="modal-field">
                    <label htmlFor="modal-power-source">
                      Power Source <span className="required-mark" aria-hidden="true">*</span>
                    </label>
                    <select
                      id="modal-power-source"
                      name="modalSelectedPowerSourceId"
                      value={values.modalSelectedPowerSourceId}
                      onChange={(event) => setField("modalSelectedPowerSourceId", event.target.value)}
                    >
                      {catalogue.powerSources.map((powerSource) => (
                        <option key={powerSource.id} value={powerSource.id}>
                          {powerSource.name} ({powerSource.powerSourceRatingKva} kVA)
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h3 className="modal-section-title">Field joint geometry</h3>
                <div className="modal-fields-grid modal-fields-grid--5col">
                  <div className="modal-field">
                    <label htmlFor="modal-pipe-od">
                      Pipe Outside Diameter <span className="required-mark" aria-hidden="true">*</span>
                    </label>
                    <InputWithUnitSelect
                      id="modal-pipe-od"
                      name="modalPipeOutsideDiameter"
                      value={values.modalPipeOutsideDiameter}
                      unit={values.modalPipeOutsideDiameterUnit}
                      unitOptions={[
                        { value: "mm", label: "mm" },
                        { value: "inch", label: "inch" },
                      ]}
                      onValueChange={(value) => setField("modalPipeOutsideDiameter", value)}
                      onUnitChange={(unit) =>
                        handleGeometryUnitChange(
                          "modalPipeOutsideDiameter",
                          "modalPipeOutsideDiameterUnit",
                          unit,
                        )
                      }
                    />
                    {fieldErrors.modalPipeOutsideDiameter && (
                      <span className="field-error" role="alert">
                        {fieldErrors.modalPipeOutsideDiameter}
                      </span>
                    )}
                  </div>

                  <div className="modal-field">
                    <label htmlFor="modal-wall-thickness">
                      Pipe Wall Thickness <span className="required-mark" aria-hidden="true">*</span>
                    </label>
                    <InputWithUnitSelect
                      id="modal-wall-thickness"
                      name="modalPipeWallThickness"
                      value={values.modalPipeWallThickness}
                      unit={values.modalPipeWallThicknessUnit}
                      unitOptions={[
                        { value: "mm", label: "mm" },
                        { value: "inch", label: "inch" },
                      ]}
                      onValueChange={(value) => setField("modalPipeWallThickness", value)}
                      onUnitChange={(unit) =>
                        handleGeometryUnitChange(
                          "modalPipeWallThickness",
                          "modalPipeWallThicknessUnit",
                          unit,
                        )
                      }
                    />
                    {fieldErrors.modalPipeWallThickness && (
                      <span className="field-error" role="alert">
                        {fieldErrors.modalPipeWallThickness}
                      </span>
                    )}
                  </div>

                  <div className="modal-field">
                    <label htmlFor="modal-coating-thickness">
                      Factory Coating Thickness <span className="required-mark" aria-hidden="true">*</span>
                    </label>
                    <InputWithUnitSelect
                      id="modal-coating-thickness"
                      name="modalFactoryCoatingThickness"
                      value={values.modalFactoryCoatingThickness}
                      unit={values.modalFactoryCoatingThicknessUnit}
                      step="1"
                      unitOptions={[
                        { value: "um", label: "µm" },
                        { value: "mm", label: "mm" },
                        { value: "inch", label: "inch" },
                      ]}
                      onValueChange={(value) => setField("modalFactoryCoatingThickness", value)}
                      onUnitChange={(unit) =>
                        handleGeometryUnitChange(
                          "modalFactoryCoatingThickness",
                          "modalFactoryCoatingThicknessUnit",
                          unit,
                        )
                      }
                    />
                    {fieldErrors.modalFactoryCoatingThickness && (
                      <span className="field-error" role="alert">
                        {fieldErrors.modalFactoryCoatingThickness}
                      </span>
                    )}
                  </div>

                  <div className="modal-field">
                    <label htmlFor="modal-side-a-cutback">
                      Side A Cutback <span className="required-mark" aria-hidden="true">*</span>
                    </label>
                    <InputWithUnitSelect
                      id="modal-side-a-cutback"
                      name="modalSideACutback"
                      value={values.modalSideACutback}
                      unit={values.modalSideACutbackUnit}
                      unitOptions={[
                        { value: "mm", label: "mm" },
                        { value: "inch", label: "inch" },
                      ]}
                      onValueChange={(value) => setField("modalSideACutback", value)}
                      onUnitChange={(unit) =>
                        handleGeometryUnitChange("modalSideACutback", "modalSideACutbackUnit", unit)
                      }
                    />
                    {fieldErrors.modalSideACutback && (
                      <span className="field-error" role="alert">
                        {fieldErrors.modalSideACutback}
                      </span>
                    )}
                  </div>

                  <div className="modal-field">
                    <label htmlFor="modal-side-b-cutback">
                      Side B Cutback <span className="required-mark" aria-hidden="true">*</span>
                    </label>
                    <InputWithUnitSelect
                      id="modal-side-b-cutback"
                      name="modalSideBCutback"
                      value={values.modalSideBCutback}
                      unit={values.modalSideBCutbackUnit}
                      unitOptions={[
                        { value: "mm", label: "mm" },
                        { value: "inch", label: "inch" },
                      ]}
                      onValueChange={(value) => setField("modalSideBCutback", value)}
                      onUnitChange={(unit) =>
                        handleGeometryUnitChange("modalSideBCutback", "modalSideBCutbackUnit", unit)
                      }
                    />
                    {fieldErrors.modalSideBCutback && (
                      <span className="field-error" role="alert">
                        {fieldErrors.modalSideBCutback}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h3 className="modal-section-title">Heating</h3>
                <div className="modal-fields-3col-grid">
                  <div className="modal-field">
                    <label htmlFor="modal-ambient-temp">
                      Ambient Temperature °C <span className="required-mark" aria-hidden="true">*</span>
                    </label>
                    <input
                      type="number"
                      id="modal-ambient-temp"
                      name="modalAmbientTemperatureC"
                      step="0.1"
                      value={values.modalAmbientTemperatureC}
                      onChange={(event) => setField("modalAmbientTemperatureC", event.target.value)}
                    />
                    {fieldErrors.modalAmbientTemperatureC && (
                      <span className="field-error" role="alert">
                        {fieldErrors.modalAmbientTemperatureC}
                      </span>
                    )}
                  </div>
                  <div className="modal-field">
                    <label htmlFor="modal-starting-temp">Starting Temperature °C</label>
                    <input
                      type="number"
                      id="modal-starting-temp"
                      name="modalStartingTemperatureC"
                      step="0.1"
                      placeholder="Defaults to ambient"
                      value={values.modalStartingTemperatureC}
                      onChange={(event) => setField("modalStartingTemperatureC", event.target.value)}
                    />
                  </div>
                  <div className="modal-field">
                    <label htmlFor="modal-target-temp">
                      Target Heat Temperature °C <span className="required-mark" aria-hidden="true">*</span>
                    </label>
                    <input
                      type="number"
                      id="modal-target-temp"
                      name="modalTargetHeatTemperatureC"
                      step="0.1"
                      value={values.modalTargetHeatTemperatureC}
                      onChange={(event) => setField("modalTargetHeatTemperatureC", event.target.value)}
                    />
                    {fieldErrors.modalTargetHeatTemperatureC && (
                      <span className="field-error" role="alert">
                        {fieldErrors.modalTargetHeatTemperatureC}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h3 className="modal-section-title">Optional</h3>
                <div className="modal-fields-grid">
                  <div className="modal-field">
                    <label htmlFor="modal-app-product">Application Product Name</label>
                    <input
                      type="text"
                      id="modal-app-product"
                      name="modalApplicationProductName"
                      value={values.modalApplicationProductName}
                      onChange={(event) => setField("modalApplicationProductName", event.target.value)}
                    />
                  </div>
                  <div className="modal-field">
                    <label htmlFor="modal-subsequent-temp">Subsequent Process Temperature °C</label>
                    <input
                      type="number"
                      id="modal-subsequent-temp"
                      name="modalSubsequentProcessTemperatureC"
                      step="0.1"
                      value={values.modalSubsequentProcessTemperatureC}
                      onChange={(event) =>
                        setField("modalSubsequentProcessTemperatureC", event.target.value)
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {formError && (
              <div id="configuration-modal-errors" className="error-output" role="alert">
                {formError}
              </div>
            )}
          </form>
        </div>

        <div className="dialog-footer">
          <CcButton variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </CcButton>
          <CcButton
            variant="primary"
            className="configuration-modal__submit"
            type="submit"
            form="configuration-modal-form"
            loading={submitting}
          >
            {submitLabel}
          </CcButton>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  open: state.coilConfigurator.ui.configModal.open,
  mode: state.coilConfigurator.ui.configModal.mode,
  editId: state.coilConfigurator.ui.configModal.editId,
  catalogue: state.coilConfigurator.catalogue.active,
  configurations: state.coilConfigurator.configurations.items,
  fieldErrors: state.coilConfigurator.ui.configModal.fieldErrors,
  formError: state.coilConfigurator.ui.configModal.formError,
  submitting: state.coilConfigurator.ui.configModal.submitting,
});

const mapDispatchToProps = {
  onClose: closeConfigModal,
  onSubmit: submitConfigModal,
  onClearErrors: clearConfigModalErrors,
};

export default connect(mapStateToProps, mapDispatchToProps)(ConfigurationModal);
